import type { BoxProps } from '@mui/material/Box';
import type { Breakpoint } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { RouterLink } from 'src/routes/components';

import { CONFIG } from 'src/config-global';
import { varAlpha, bgGradient } from 'src/theme/styles';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router';
import { useAuthContext } from 'src/context/AuthContext';
import AuthUtils from 'src/utils/AuthUtils';
import { Button } from '@mui/material';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import FormConstants from 'src/constants/FormConstants';
import { signOutUser } from 'src/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import { toast } from 'src/components/snackbar';
import ParkCityLogo from 'src/components/logo/logo_new';

// ----------------------------------------------------------------------

type SectionProps = BoxProps & {
  title?: string;
  method?: string;
  imgUrl?: string | boolean;
  subtitle?: string;
  bgImage?: string;
  layoutQuery: Breakpoint;
  customSection?: any;
  renderBackButton?: boolean;
  backButtonPage?:string;
  showLogout?: boolean;
  methods?: {
    path: string;
    icon: string;
    label: string;
  }[];
};

export function Section({
  sx,
  method,
  layoutQuery,
  methods,
  title = 'Manage the job',
  imgUrl = `${CONFIG.assetsDir}/assets/illustrations/illustration-dashboard.webp`,
  subtitle = 'More effectively with optimized workflows.',
  bgImage,
  customSection,
  renderBackButton = true,
  backButtonPage,
  showLogout = false,
  ...other
}: SectionProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuthContext();
  const isAzureAuthenticated = useIsAuthenticated();

  const { instance } = useMsal();

  const { authState } = useAuthContext();
  const { isAdmin, isAuthenticated } = authState;

  const logoTarget = isAuthenticated ? (isAdmin ? '/profile' : '/applications') : '/';

  const handleLogOut = async () => {
    try {
      const data: any = {
        sql_data: {
          is_audit: true,
          audit_event_description: FormConstants.COMMON.LOGOUT_SUCCESS,
        },
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      await signOutUser({
        tk: encryptRequestData,
      });
      if (isAzureAuthenticated || AuthUtils.getIsAzureAuthenticated()) {
        instance.logoutRedirect({
          postLogoutRedirectUri: '/',
        });
      } else {
        logout();
      }
      AuthUtils.clearStorage();

      // Redirect based on admin status
      navigate(isAdmin ? '/admin/sign-in' : '/sign-in', { replace: true });
    } catch (e) {
      const errMessage = getErrorMessage(e);
      toast.error(errMessage);
    }
  };

  return (
    <Box
      sx={{
        ...(bgImage
          ? {
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${bgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: '0 0',
                backgroundAttachment: 'fixed',
                willChange: 'background-image',
                filter: theme.palette.mode === 'dark' ? 'brightness(0.4)' : 'none',
                zIndex: 0,
                pointerEvents: 'none',
              },
            }
          : bgGradient({
              color: `0deg, ${varAlpha(
                theme.vars.palette.background.defaultChannel,
                0.92
              )}, ${varAlpha(theme.vars.palette.background.defaultChannel, 0.92)}`,
              imgUrl: `${CONFIG.assetsDir}/assets/background/background-3-blur.webp`,
            })),
        px: 3,
        pb: 3,
        width: 1,
        maxWidth: '25vw',
        display: 'none',
        position: 'relative',
        pt: 'var(--layout-header-desktop-height)',
        pointerEvents: 'auto',
        [theme.breakpoints.up(layoutQuery)]: {
          gap: 8,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          justifyContent: 'center',
        },
        ...sx,
      }}
      {...other}
    >
      {renderBackButton && (
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            top: 'var(--layout-header-desktop-height)',
            width: '90%',
            cursor: 'pointer',
          }}
          onClick={() => {
            if(backButtonPage) {
              navigate(backButtonPage);
            } else {
              navigate(-1);
            }
          }}
        >
          <Typography
            variant="h5"
            color="white"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Icon icon="ei:arrow-left" height={32} /> Back
          </Typography>
        </Box>
      )}
      {!customSection && (
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" sx={{ textAlign: 'center' }}>
            {title}
          </Typography>

          {subtitle && (
            <Typography sx={{ color: 'text.secondary', textAlign: 'center', mt: 2 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      {/* Logo - Render FIRST to ensure it's on top and clickable */}
      <Box
        component="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          navigate(logoTarget);
        }}
        sx={{
          height: '3rem',
          width: 'auto',
          minWidth: '200px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          textDecoration: 'none',
          position: 'absolute',
          top: 15,
          left: 25,
          zIndex: 10000,
          cursor: 'pointer',
          pointerEvents: 'auto',
          userSelect: 'none',
          border: 'none',
          background: 'transparent',
          padding: 0,
          margin: 0,
          '&:hover': {
            opacity: 0.8,
          },
          '&:focus': {
            outline: 'none',
          },
        }}
      >
        <ParkCityLogo />
      </Box>

      {customSection && <Box sx={{ position: 'relative', zIndex: 1 }}>{customSection}</Box>}

      {typeof imgUrl === 'string' && (
        <Box
          component="img"
          alt="Dashboard illustration"
          src={imgUrl}
          sx={{ width: 1, aspectRatio: '4/3', objectFit: 'cover', position: 'relative', zIndex: 1 }}
        />
      )}

      {!!methods?.length && method && (
        <Box component="ul" gap={2} display="flex" sx={{ position: 'relative', zIndex: 1 }}>
          {methods.map((option) => {
            const selected = method === option.label.toLowerCase();

            return (
              <Box
                key={option.label}
                component="li"
                sx={{
                  ...(!selected && {
                    cursor: 'not-allowed',
                    filter: 'grayscale(1)',
                  }),
                }}
              >
                <Tooltip title={option.label} placement="top">
                  <Link
                    component={RouterLink}
                    href={option.path}
                    sx={{
                      ...(!selected && { pointerEvents: 'none' }),
                    }}
                  >
                    <Box
                      component="img"
                      alt={option.label}
                      src={option.icon}
                      sx={{ width: 32, height: 32 }}
                    />
                  </Link>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}
      {showLogout && (
        <Button
          startIcon={<Icon icon="tabler:logout-2" color="black" />}
          variant="contained"
          sx={{
            width: '60%',
            position: 'absolute',
            bottom: 20,
            backgroundColor: '#f0eff4',
            color: 'black',
            mx: 5,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1,
            '&:hover': {
              backgroundColor: '#f8eeec', // Change background color on hover
              color: '#000000', // Change text color on hover
            },
          }}
          onClick={handleLogOut}
        >
          Logout
        </Button>
      )}
    </Box>
  );
}
